import { Modal, Typography, Flex, Spin, Table, Button, message } from 'antd';
import { SvgIcon } from '@/components/icon';
import './index.scss';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getConsultantReviewsForFranchise,
  getLatestConsultantReviewSummaryForFranchise,
  downloadConsultantReviewsForFranchise,
} from '@/api/admin';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { CloudDownloadOutlined, ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { SortOrder } from '@/types';

interface Props {
  open: boolean;
  onClose: () => void;
  franchisorId: string;
  name: string;
}

const renderStars = (rating: number, size: number = 16) => {
  const roundedUp = Math.floor(rating);

  return (
    <Flex gap={4}>
      {Array.from({ length: 5 }).map((_, i) => (
        <SvgIcon
          key={i}
          type={i < roundedUp ? 'goldStar' : 'grayStar'}
          width={size}
          height={size}
        />
      ))}
    </Flex>
  );
};

export default function RatingDetailsModal({
  open,
  onClose,
  franchisorId,
  name,
}: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const pageSize = 10;

  const { data, isLoading } = useQuery({
    queryKey: ['franchisor-review', franchisorId],
    queryFn: () => getLatestConsultantReviewSummaryForFranchise(franchisorId),
    enabled: open && !!franchisorId,
  });

  const { data: reviewsData, isLoading: isReviewsLoading } = useQuery({
    queryKey: ['franchisor-reviews-list', franchisorId, currentPage, sortOrder],
    queryFn: () =>
      getConsultantReviewsForFranchise(franchisorId, {
        page: currentPage,
        limit: pageSize,
        sort: sortOrder,
      }),
    enabled: open && !!franchisorId,
  });

  useEffect(() => {
    if (open) return;
    queryClient.removeQueries({
      queryKey: ['franchisor-review', franchisorId],
      exact: true,
    });
    queryClient.removeQueries({
      queryKey: ['franchisor-reviews-list', franchisorId],
      exact: true,
    });
  }, [open, franchisorId, queryClient]);

  const handleClose = () => {
    queryClient.removeQueries({
      queryKey: ['franchisor-review', franchisorId],
      exact: true,
    });
    queryClient.removeQueries({
      queryKey: ['franchisor-reviews-list', franchisorId],
      exact: false,
    });
    setCurrentPage(1);
    setSortOrder('desc');
    onClose();
  };
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadTable = async () => {
    try {
      setIsDownloading(true);
      const { file: base64Response } = await downloadConsultantReviewsForFranchise(
        franchisorId,
        { sort: sortOrder },
      );

      if (!base64Response || typeof base64Response !== 'string') {
        throw new Error('Invalid file data');
      }

      const csvContent = atob(base64Response);
      const blob = new Blob([csvContent], { type: 'text/csv' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `consultant_reviews_${name}_${new Date().toISOString()}.csv`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      message.success('File downloaded successfully');
    } catch (error: any) {
      console.error(error);
      message.error(error.message || 'Failed to download file.');
    } finally {
      setIsDownloading(false);
    }
  };

  const breakdown = [
    {
      label: 'Q1',
      question: 'How strong do you feel the franchise opportunity is?',
      value: data?.ratingBreakdown?.franchiseOpportunityStrength ?? 0,
    },
    {
      label: 'Q2',
      question:
        'How effective/capable do you feel the franchise development rep you spoke with is?',
      value: data?.ratingBreakdown?.repEffectiveness ?? 0,
    },
    {
      label: 'Q3',
      question:
        'How likely are you to recommend their franchise to one of your candidates?',
      value: data?.ratingBreakdown?.recommendationLikelihood ?? 0,
    },
  ];

  const columns = [
    {
      title: 'REVIEWER',
      dataIndex: 'consultant',
      key: 'reviewer',
      render: (consultant: any) => (
        <Typography.Text>
          {consultant?.firstName} {consultant?.lastName}
        </Typography.Text>
      ),
    },
    {
      title: 'DATE',
      dataIndex: 'createdAt',
      key: 'date',
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      title: 'Q1',
      key: 'q1',
      render: (_: any, record: any) => {
        const q1Rating =
          record.ratings?.find(
            (r: any) => r.questionId === 'franchiseOpportunityStrength',
          )?.rating ?? 0;
        return (
          <Flex gap={8} align="center">
            {renderStars(q1Rating, 14)}
            <Typography.Text>{q1Rating > 0 ? q1Rating.toFixed(1) : '0'}</Typography.Text>
          </Flex>
        );
      },
    },
    {
      title: 'Q2',
      key: 'q2',
      render: (_: any, record: any) => {
        const q2Rating =
          record.ratings?.find((r: any) => r.questionId === 'repEffectiveness')
            ?.rating ?? 0;
        return (
          <Flex gap={8} align="center">
            {renderStars(q2Rating, 14)}
            <Typography.Text>{q2Rating > 0 ? q2Rating.toFixed(1) : '0'}</Typography.Text>
          </Flex>
        );
      },
    },
    {
      title: 'Q3',
      key: 'q3',
      render: (_: any, record: any) => {
        const q3Rating =
          record.ratings?.find(
            (r: any) => r.questionId === 'recommendationLikelihood',
          )?.rating ?? 0;
        return (
          <Flex gap={7} align="center">
            {renderStars(q3Rating, 14)}
            <Typography.Text>{q3Rating > 0 ? q3Rating.toFixed(1) : '0'}</Typography.Text>
          </Flex>
        );
      },
    },
    {
      title: 'ADDITIONAL FEEDBACK',
      dataIndex: 'comment',
      key: 'feedback',
      width: '30%',
      render: (comment: string) => (
        <Typography.Text>
          {comment || '-'}
        </Typography.Text>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      destroyOnClose
      footer={null}
      width={1200}
      centered
      title={`Rating Details for ${name}`}
      className={'rating-detail-modal-v2'}
    >
      {isLoading ? (
        <Flex align="center" justify="center" style={{ padding: 48 }}>
          <Spin size="large" />
        </Flex>
      ) : (
        <div className={'rating-modal-v2'}>
          {/* NPS Section */}
          <div className={'nps-container'}>
            <Flex align="center" justify="center" gap={32}>
              <Typography.Text className={'nps-label'}>
                Net Promoter Score (Q3)
              </Typography.Text>
              <Flex gap={8} align="center">
                {renderStars(data?.nps ?? 0, 25)}
                <Typography.Text className={'nps-value'}>
                  {(Math.floor((data?.nps ?? 0) * 10) / 10).toFixed(1)}
                </Typography.Text>
              </Flex>
            </Flex>
          </div>

          {/* Average Scores Section */}
          <div className={'average-scores-section'}>
            <Typography.Text
              style={{ marginBottom: 23 }}
              className={'section-title'}
            >
              Average Score
            </Typography.Text>
            <div className={'scores-grid'}>
              {breakdown.map((item, i) => (
                <div key={i} className={'score-card'}>
                  <Typography.Text className={'card-question'}>
                    <span className="card-label">{item.label}-</span>{' '}
                    {item.question}
                  </Typography.Text>
                  <Flex
                    justify="space-between"
                    align="center"
                    style={{ marginTop: 'auto' }}
                  >
                    <Flex gap={8} align="center">
                      {renderStars(item.value, 16)}
                    </Flex>
                    <Flex gap={4} align="center">
                      <Typography.Text strong>
                        {item.value.toFixed(1)}
                      </Typography.Text>
                      <Typography.Text className={'review-count'}>
                        ({data?.totalReviews ?? 0} reviews)
                      </Typography.Text>
                    </Flex>
                  </Flex>
                </div>
              ))}
            </div>
          </div>

          {/* All Reviews Table Section */}
          <div className={'reviews-table-section'}>
            <Flex
              justify="space-between"
              align="center"
              style={{ marginBottom: 16 }}
            >
              <Typography.Text className={'section-title'}>
                All Reviews
              </Typography.Text>
              <Flex gap={10}>
                <Button
                  className="sort-button"
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  icon={
                    sortOrder === 'desc' ? (
                      <ArrowDownOutlined style={{ fontSize: 16 }} />
                    ) : (
                      <ArrowUpOutlined style={{ fontSize: 16 }} />
                    )
                  }
                >
                  Date
                </Button>
                <Button
                  icon={<CloudDownloadOutlined style={{ fontSize: 16 }} />}
                  className="admin-button-download-table"
                  type="default"
                  loading={isDownloading}
                  onClick={handleDownloadTable}
                >
                  Download Reviews
                </Button>
              </Flex>
            </Flex>

            <Table
              loading={isReviewsLoading}
              dataSource={reviewsData?.reviews ?? []}
              columns={columns}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: reviewsData?.total ?? 0,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
                position: ['bottomCenter'],
              }}
              className="reviews-table"
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
